import { observer } from "mobx-react-lite";
import { FC, FormEvent, useEffect, useRef, useState } from "react";
import { appRoutes } from "@/app/router";
import { AuthCardBase } from "@/components/auth/auth-card-base";
import { usePublicAppStore } from "@/store";
import {
  MDSButton,
  MDSButtonVariant,
  MDSText,
  MDSTextColor,
  MDSTextField,
  MDSTextFieldVariant,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "@emotion/styled";
import { readIMessageClaimFlowSession } from "@/modules/mem-agent/imessage-claim-flow-session";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";

export interface AuthCardLogInProps {}

type EmailPasswordAuthMode = "continue" | "login" | "signup" | "forgot" | "reset" | "verify";

const EMAIL_PASSWORD_MIN_LENGTH = 8;
const PASSWORD_REQUIREMENT_MESSAGE = `Password must be at least ${EMAIL_PASSWORD_MIN_LENGTH} characters`;

const parseAuthMode = (mode: string | null): EmailPasswordAuthMode => {
  if (
    mode === "continue" ||
    mode === "signup" ||
    mode === "forgot" ||
    mode === "reset" ||
    mode === "verify"
  ) {
    return mode;
  }

  return "login";
};

export const AuthCardEmailPassword: FC<AuthCardLogInProps> = observer(() => {
  const { publicStore } = usePublicAppStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [mode, setModeState] = useState<EmailPasswordAuthMode>(
    parseAuthMode(searchParams.get("mode"))
  );
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [canRequestVerification, setCanRequestVerification] = useState<boolean>(false);
  const [signupAwaitingVerification, setSignupAwaitingVerification] = useState<boolean>(false);
  const verificationAttemptKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const emailFromParams = searchParams.get("email");

    if (emailFromParams) {
      setEmail(emailFromParams);
    }
  }, [searchParams]);

  useEffect(() => {
    if (mode !== "verify") {
      return;
    }

    const emailFromParams = searchParams.get("email");
    const tokenFromParams = searchParams.get("token");
    const verificationAttemptKey = `${emailFromParams ?? ""}:${tokenFromParams ?? ""}`;

    if (verificationAttemptKeyRef.current === verificationAttemptKey) {
      return;
    }

    verificationAttemptKeyRef.current = verificationAttemptKey;

    if (!emailFromParams || !tokenFromParams) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "verification",
        outcome: "validation_failed",
        failure_reason: "missing_input",
      });
      setErrorMessage("This verification link is missing required information");
      return;
    }

    const verifyEmail = async () => {
      setIsSubmitting(true);
      setErrorMessage(null);
      setFeedbackMessage(null);
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "verification",
        outcome: "submitted",
      });

      const result = await publicStore.auth.verifyEmailPasswordEmailAddress({
        email: emailFromParams,
        token: tokenFromParams,
      });

      if (!result.success) {
        trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
          step: "verification",
          outcome: "failed",
          failure_reason: "request_failed",
        });
        setErrorMessage(result.errorMessage ?? "Failed to verify email");
        setIsSubmitting(false);
        return;
      }

      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "verification",
        outcome: "succeeded",
      });
      setFeedbackMessage("Email verified. You can log in with your password.");
      setIsSubmitting(false);
    };

    void verifyEmail();
  }, [mode, publicStore.auth, searchParams]);

  const setMode = (nextMode: EmailPasswordAuthMode) => {
    setModeState(nextMode);
    setPassword("");
    setConfirmPassword("");
    setErrorMessage(null);
    setFeedbackMessage(null);
    setCanRequestVerification(false);
    setSignupAwaitingVerification(false);
    const nextSearchParams: Record<string, string> = {};
    if (nextMode !== "login") {
      nextSearchParams.mode = nextMode;
    }
    if (email) {
      nextSearchParams.email = email;
    }
    setSearchParams(nextSearchParams, { replace: true });
  };

  const handleAllLoginOptions = () => {
    navigate(appRoutes.logIn({}).path);
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim();

    if (!trimmedEmail || !password) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "login_password",
        outcome: "validation_failed",
        failure_reason: "missing_input",
      });
      setErrorMessage("Enter your email and password");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setFeedbackMessage(null);
    setCanRequestVerification(false);
    trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
      step: "login_password",
      outcome: "submitted",
    });

    const result = await publicStore.auth.authenticateAccountUsingEmailPassword({
      email: trimmedEmail,
      password,
      includeNativeChatOnboarding: !readIMessageClaimFlowSession(),
    });

    if (!result.success) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "login_password",
        outcome: "failed",
        failure_reason: "request_failed",
      });
      const nextErrorMessage = result.errorMessage ?? "Failed to log in";
      setErrorMessage(nextErrorMessage);
      setCanRequestVerification(nextErrorMessage.toLowerCase().includes("not verified"));
      setIsSubmitting(false);
      return;
    }

    trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
      step: "login_password",
      outcome: "succeeded",
    });
    setIsSubmitting(false);
  };

  const handleContinue = async (e: FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim();

    if (!trimmedEmail || !password) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "password",
        outcome: "validation_failed",
        failure_reason: "missing_input",
      });
      setErrorMessage("Enter your email and password");
      return;
    }

    if (password.length < EMAIL_PASSWORD_MIN_LENGTH) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "password",
        outcome: "validation_failed",
        failure_reason: "weak_password",
      });
      setErrorMessage(PASSWORD_REQUIREMENT_MESSAGE);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setFeedbackMessage(null);
    setCanRequestVerification(false);
    trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
      step: "password",
      outcome: "submitted",
    });

    const loginResult = await publicStore.auth.authenticateAccountUsingEmailPassword({
      email: trimmedEmail,
      password,
      includeNativeChatOnboarding: !readIMessageClaimFlowSession(),
    });

    if (loginResult.success) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "password",
        outcome: "succeeded",
        next_step: "authenticated",
      });
      setIsSubmitting(false);
      return;
    }

    const loginErrorMessage = loginResult.errorMessage ?? "Failed to continue";
    if (loginErrorMessage.toLowerCase().includes("not verified")) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "password",
        outcome: "failed",
        failure_reason: "request_failed",
      });
      setErrorMessage(loginErrorMessage);
      setCanRequestVerification(true);
      setIsSubmitting(false);
      return;
    }

    const signupResult = await publicStore.auth.signUpUsingEmailPassword({
      email: trimmedEmail,
      password,
    });

    if (!signupResult.success) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "password",
        outcome: "failed",
        failure_reason: "request_failed",
      });
      setErrorMessage(signupResult.errorMessage ?? "Failed to continue");
      setIsSubmitting(false);
      return;
    }

    trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
      step: "password",
      outcome: "succeeded",
      next_step: "email_sent",
    });
    setFeedbackMessage("Check your email to continue");
    setSignupAwaitingVerification(true);
    setPassword("");
    setConfirmPassword("");
    setIsSubmitting(false);
  };

  const handleSignup = async (e: FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim();

    if (!trimmedEmail || !password) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "signup_password",
        outcome: "validation_failed",
        failure_reason: "missing_input",
      });
      setErrorMessage("Enter an email and password");
      return;
    }

    if (password.length < EMAIL_PASSWORD_MIN_LENGTH) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "signup_password",
        outcome: "validation_failed",
        failure_reason: "weak_password",
      });
      setErrorMessage(PASSWORD_REQUIREMENT_MESSAGE);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setFeedbackMessage(null);
    trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
      step: "signup_password",
      outcome: "submitted",
    });

    const result = await publicStore.auth.signUpUsingEmailPassword({
      email: trimmedEmail,
      password,
    });

    if (!result.success) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "signup_password",
        outcome: "failed",
        failure_reason: "request_failed",
      });
      setErrorMessage(result.errorMessage ?? "Failed to create account");
      setIsSubmitting(false);
      return;
    }

    trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
      step: "signup_password",
      outcome: "succeeded",
    });
    setFeedbackMessage("Check your email to verify your account before logging in");
    setCanRequestVerification(true);
    setSignupAwaitingVerification(true);
    setPassword("");
    setConfirmPassword("");
    setIsSubmitting(false);
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "password_reset_request",
        outcome: "validation_failed",
        failure_reason: "missing_input",
      });
      setErrorMessage("Enter your email");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setFeedbackMessage(null);
    trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
      step: "password_reset_request",
      outcome: "submitted",
    });

    const result = await publicStore.auth.requestEmailPasswordReset({
      email: trimmedEmail,
    });

    if (!result.success) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "password_reset_request",
        outcome: "failed",
        failure_reason: "request_failed",
      });
      setErrorMessage(result.errorMessage ?? "Failed to send password reset email");
      setIsSubmitting(false);
      return;
    }

    trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
      step: "password_reset_request",
      outcome: "succeeded",
    });
    setFeedbackMessage("If an account exists for that email, a reset link has been sent");
    setIsSubmitting(false);
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim();
    const token = searchParams.get("token");

    if (!trimmedEmail || !token) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "password_reset",
        outcome: "validation_failed",
        failure_reason: "missing_input",
      });
      setErrorMessage("This reset link is missing required information");
      return;
    }

    if (!password) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "password_reset",
        outcome: "validation_failed",
        failure_reason: "missing_input",
      });
      setErrorMessage("Enter a new password");
      return;
    }

    if (password.length < EMAIL_PASSWORD_MIN_LENGTH) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "password_reset",
        outcome: "validation_failed",
        failure_reason: "weak_password",
      });
      setErrorMessage(PASSWORD_REQUIREMENT_MESSAGE);
      return;
    }

    if (password !== confirmPassword) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "password_reset",
        outcome: "validation_failed",
        failure_reason: "password_mismatch",
      });
      setErrorMessage("Passwords do not match");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setFeedbackMessage(null);
    trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
      step: "password_reset",
      outcome: "submitted",
    });

    const result = await publicStore.auth.resetEmailPassword({
      email: trimmedEmail,
      token,
      password,
    });

    if (!result.success) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "password_reset",
        outcome: "failed",
        failure_reason: "request_failed",
      });
      setErrorMessage(result.errorMessage ?? "Failed to reset password");
      setIsSubmitting(false);
      return;
    }

    trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
      step: "password_reset",
      outcome: "succeeded",
    });
    setModeState("login");
    setSearchParams({ email: trimmedEmail }, { replace: true });
    setPassword("");
    setConfirmPassword("");
    setFeedbackMessage("Password reset. You can log in with your new password.");
    setIsSubmitting(false);
  };

  const handleRequestVerification = async () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "verification_resend",
        outcome: "validation_failed",
        failure_reason: "missing_input",
      });
      setErrorMessage("Enter your email");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setFeedbackMessage(null);
    trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
      step: "verification_resend",
      outcome: "submitted",
    });

    const result = await publicStore.auth.requestEmailPasswordVerification({
      email: trimmedEmail,
    });

    if (!result.success) {
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "verification_resend",
        outcome: "failed",
        failure_reason: "request_failed",
      });
      setErrorMessage(result.errorMessage ?? "Failed to send verification email");
      setIsSubmitting(false);
      return;
    }

    trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
      step: "verification_resend",
      outcome: "succeeded",
    });
    setFeedbackMessage("Verification email sent");
    setIsSubmitting(false);
  };

  const authStoreLoading = publicStore.auth.authenticateAccountUsingEmailPasswordState.isLoading;
  const isLoading = authStoreLoading || isSubmitting;
  const shouldShowFormFields = mode !== "verify" && !signupAwaitingVerification;

  const navigationActions = (() => {
    const tertiaryActions: { label: string; onPress: () => void }[] = [];

    if (mode === "verify" && feedbackMessage) {
      tertiaryActions.push({
        label: "Continue",
        onPress: () => setMode("login"),
      });
    } else if (mode === "forgot") {
      tertiaryActions.push({
        label: "Back to password",
        onPress: () => setMode("login"),
      });
    } else if (mode === "continue" || mode === "login") {
      tertiaryActions.push({
        label: "Forgot password?",
        onPress: () => setMode("forgot"),
      });
    }

    if (mode === "continue" || mode === "login" || mode === "signup") {
      tertiaryActions.push({
        label: "Use a different email address",
        onPress: handleAllLoginOptions,
      });
    } else if (mode !== "verify" || !feedbackMessage) {
      tertiaryActions.push({
        label: "Back to login",
        onPress: handleAllLoginOptions,
      });
    }

    return { tertiaryActions };
  })();

  const formSubmitHandler = {
    continue: handleContinue,
    login: handleLogin,
    signup: handleSignup,
    forgot: handleForgotPassword,
    reset: handleResetPassword,
    verify: undefined,
  }[mode];

  const heading = {
    continue: "Continue with your work email",
    login: "Enter your password",
    signup: "Create a password",
    forgot: "Reset your password",
    reset: "Choose a new password",
    verify: "Verify your email",
  }[mode];

  return (
    <AuthCardBase>
      <AuthForm onSubmit={formSubmitHandler}>
        <FormHeading variant={MDSTextVariant.TextSm} weight={MDSTextWeight.Medium}>
          {heading}
        </FormHeading>
        {feedbackMessage && (
          <StatusText
            role="status"
            variant={MDSTextVariant.ParagraphS}
            color={MDSTextColor.Primary}
          >
            {feedbackMessage}
          </StatusText>
        )}
        {errorMessage && (
          <StatusText role="alert" variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Danger}>
            {errorMessage}
          </StatusText>
        )}
        {shouldShowFormFields && (
          <>
            <MDSTextField
              variant={MDSTextFieldVariant.Form}
              type="email"
              placeholder="Work email address"
              value={email}
              name="Work email address"
              onChange={value => setEmail(value)}
              isDisabled={
                isLoading ||
                ((mode === "continue" || mode === "login" || mode === "signup") && Boolean(email))
              }
              aria-label="Work email address"
            />
            {mode !== "forgot" && (
              <MDSTextField
                variant={MDSTextFieldVariant.Form}
                type="password"
                placeholder={mode === "reset" ? "New password" : "Password"}
                name="Password"
                value={password}
                aria-label={mode === "reset" ? "New password" : "Password"}
                onChange={value => setPassword(value)}
                isDisabled={isLoading}
                autoFocus
              />
            )}
            {mode === "reset" && (
              <MDSTextField
                variant={MDSTextFieldVariant.Form}
                type="password"
                placeholder="Confirm password"
                name="Confirm password"
                value={confirmPassword}
                aria-label="Confirm password"
                onChange={value => setConfirmPassword(value)}
                isDisabled={isLoading}
              />
            )}
            <FullWidthButton
              variant={MDSButtonVariant.Contrast}
              isLoading={isLoading}
              type="submit"
            >
              Continue
            </FullWidthButton>
          </>
        )}
        {canRequestVerification && (
          <FullWidthButton
            variant={MDSButtonVariant.Default}
            isLoading={isLoading}
            type="button"
            onPress={handleRequestVerification}
          >
            Resend verification email
          </FullWidthButton>
        )}
        <TertiaryActions>
          {navigationActions.tertiaryActions.map(action => (
            <TertiaryButton
              key={action.label}
              variant={MDSButtonVariant.Transparent}
              type="button"
              onPress={action.onPress}
            >
              {action.label}
            </TertiaryButton>
          ))}
        </TertiaryActions>
      </AuthForm>
    </AuthCardBase>
  );
});

const AuthForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[4]};
  max-width: 360px;
  width: 100%;

  // Targets MDSTextField container
  > div {
    width: 100%;
  }
`;

const FormHeading = styled(MDSText)`
  color: ${({ theme }) => theme.colors.text.contrast};
`;

const StatusText = styled(MDSText)`
  color: ${({ theme, color }) =>
    color === MDSTextColor.Danger ? theme.colors.text.danger : theme.colors.text.contrast};
  max-width: 360px;
`;

const FullWidthButton = styled(MDSButton)`
  width: 100%;
`;

const TertiaryActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  justify-content: center;
  width: 100%;
`;

const TertiaryButton = styled(MDSButton)`
  flex: 0 1 auto;
  max-width: 100%;
`;
